import React, { useState, useEffect } from "react";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import MapComponent from "./components/LogistMapComponent";
import api from "../../../services/api";
import TimelineModal from './TimelineModal';
//import "./styles/LogistMobile.css";
//import "./styles/LogistMainPC.css";
import styles from "./styles/LogistRoutes.module.css";



import { useSocket } from '../../../context/SocketContext';
import { useGeolocationSubscription } from '../../../hooks/useGeolocationSubscription';

// Константы для drag & drop
const COLUMN_TYPE = "COLUMN";

// Настройка дефолтных колонок
const DEFAULT_COLUMNS = [
  { key: "receipt_number", label: "№ квитанции", visible: true },
  { key: "contract_number", label: "№ договора", visible: true },
  { key: "time", label: "Время", visible: true },
  { key: "delivery_time", label: "Время доставки", visible: true },
  { key: "delivery_address", label: "Адрес доставки", visible: true },
  { key: "client_name", label: "Клиент", visible: true },
  { key: "amount", label: "Сумма", visible: true },
  { key: "to_pay", label: "К оплате", visible: true },
  { key: "paid", label: "Оплачено", visible: true },
  { key: "payment_type", label: "Тип оплаты", visible: true },
  { key: "status", label: "Статус", visible: true },
  { key: "reason", label: "Причина", visible: true },
    { key: "status_changed_at", label: "Время изменения", visible: true },
  { key: "delta_time", label: "Δ времени", visible: true },
  { key: "is_check_issued", label: "Чек", visible: true },
  { key: "scannedCount", label: "Скан", visible: true },
  { key: "products", label: "Товары", visible: true },
];

const STATUS_COLORS = {
  "Открыта": "#007bff",
  "Закрыта": "#28a745",
  "Не доставлено": "#dc3545",
  "В обработке": "#ffc107",
};

// Функция для расчёта прогресса доставки
const getProgress = (receipts) => {
  if (!Array.isArray(receipts)) {
    return { delivered: 0, inProgress: 0, undelivered: 0, totalOrders: 0 };
  }
  const totalOrders = receipts.length;
  const delivered = receipts.filter((receipt) => receipt.status === "Закрыта").length;
  const inProgress = receipts.filter((receipt) => receipt.status === "Открыта").length;
  const undelivered = receipts.filter((receipt) => receipt.status === "Не доставлено").length;
  return { delivered, inProgress, undelivered, totalOrders };
};

const LogistMain = () => {
  // Основные состояния
  const [routes, setRoutes] = useState([]);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [columns, setColumns] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortColumn, setSortColumn] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  
  // Состояния для вида и карты
  const [viewMode, setViewMode] = useState("table"); // "table" или "map"

  const [mapCenter, setMapCenter] = useState([64.543013, 40.536876]);
  const [highlightPosition, setHighlightPosition] = useState(null);

  const [showCouriers, setShowCouriers] = useState(false);
  const [courierLocations, setCourierLocations] = useState({});

  const socket = useSocket();

  const [analysisMode, setAnalysisMode] = useState(false);
  const [timeDiffs, setTimeDiffs] = useState({});

  const [showTimeline, setShowTimeline] = useState(false);
  const [includeProducts, setIncludeProducts] = useState(false);
  // 'route' — показывать один маршрут, 'all' — сразу все
  const [timelineScope, setTimelineScope] = useState('route');

  const formatDiff = (ms) => {
    const totalSec = Math.floor(ms / 1000);
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
  
    const parts = [];
    if (h > 0) parts.push(`${h}ч`);
    if (m > 0) parts.push(`${m}м`);
    // секунды выводим всегда (хотя бы "0с")
    parts.push(`${s}с`);
  
    return parts.join(' ');
  };

  // Дополнительные состояния для drag & drop (DND) и для разрешённых пользователей
  const [allowedUsersLocations, setAllowedUsersLocations] = useState({});

  // 1) Мемо/вычисление разрешённых ID для выбранного маршрута
  const permittedIds = new Set(
    (selectedRoute?.permittedUsers ?? []).map(u => u.id)
  );

  // 2) Фильтр курьеров по разрешённым пользователям
  const filteredCouriers = Object.values(courierLocations).filter(c =>
    permittedIds.has(Number(c.courierId)) || permittedIds.has(Number(c.userId))
  );
  

  // Функция для клика по маркеру (например, можно добавить навигацию)
  const handleMarkerClick = (receiptId) => {
    console.log("Клик по маркеру с ID:", receiptId);
    // Реализуйте необходимую логику, например, переход на детальную страницу
  };

  // Загрузка настроек колонок из localStorage
  useEffect(() => {
    try {
      const savedColumns = JSON.parse(localStorage.getItem("logistColumns"));
      if (savedColumns && Array.isArray(savedColumns)) {
        const mergedColumns = [
          ...savedColumns,
          ...DEFAULT_COLUMNS.filter(
            (defaultCol) => !savedColumns.find((col) => col.key === defaultCol.key)
          ),
        ];
        setColumns(mergedColumns);
        localStorage.setItem("logistColumns", JSON.stringify(mergedColumns));
      } else {
        throw new Error("Неверные данные");
      }
    } catch (error) {
      console.error(error.message);
      setColumns(DEFAULT_COLUMNS);
      localStorage.setItem("logistColumns", JSON.stringify(DEFAULT_COLUMNS));
    }
  }, []);

  useEffect(() => {
    if (columns.length > 0) {
      localStorage.setItem("logistColumns", JSON.stringify(columns));
    }
  }, [columns]);

  // Загрузка маршрутов с сервера с учетом диапазона дат
  useEffect(() => {
    const fetchRoutes = async () => {
      try {
        const response = await api.get("/routes/by-date", {
          params: {
            startDate: startDate.toISOString().split("T")[0],
            endDate: endDate.toISOString().split("T")[0],
          },
        });
        setRoutes(response || []);
      } catch (err) {
        console.error("Ошибка загрузки маршрутов", err);
      }
    };
    fetchRoutes();
  }, [startDate, endDate]);

  useGeolocationSubscription(
    false,                          // мы не публикуем свои coords
    showCouriers ? 'trackAll' : null, // режим: trackAll или отключено
    null,                           // routeId не нужен для trackAll
    (mode, enabled, reason) => {
      // обрабатываем ошибки подписки
      if (!enabled && reason) {
        alert(`Геолокация (${mode}): ${reason}`);
      }
    }
  );

  useEffect(() => {
    if (!socket) return;
    const handler = (data) => {
      // data => { courierId, username, firstName, lastName, timestamp, lat, lng, routeId? }
      const lat = Number(data.latitude ?? data.lat);
      const lng = Number(data.longitude ?? data.lng);
      const ts  = Number(data.updatedAtMs ?? data.recordedAt ?? data.timestamp);
      setCourierLocations(prev => ({
        ...prev,
        [data.courierId]: {
          ...data,
          latitude: Number.isFinite(lat) ? lat : undefined,
          longitude: Number.isFinite(lng) ? lng : undefined,
          updatedAtMs: Number.isFinite(ts)
            ? (ts < 1e12 ? ts * 1000 : ts) // на случай секундного UNIX
            : undefined,
        }
      }));
    };
    socket.on('courier:location:update', handler);
    return () => socket.off('courier:location:update', handler);
  }, [socket]);

  
  
  // Фильтрация и сортировка квитанций для выбранного маршрута
  const filteredReceipts =
    selectedRoute?.receipts.filter((receipt) =>
      Object.values(receipt)
        .filter((value) => typeof value === "string" || typeof value === "number")
        .some((value) =>
          value.toString().toLowerCase().includes(searchQuery.toLowerCase())
        )
    ) || [];

    const sortedReceipts = [...filteredReceipts].sort((a, b) => {
      if (!sortColumn) return 0;
      const getValue = (r) => {
        if (sortColumn === "status_changed_at") {
          return r.status_changed_at ? new Date(r.status_changed_at) : null;
        }
        if (sortColumn === "delta_time") {
          return timeDiffs[r.id] ?? 0;
        }
              // Для числовых полей приводим к Number
      if (["amount", "to_pay", "paid"].includes(sortColumn)) {
        const num = Number(r[sortColumn]);
        // на случай, если в данных пусто или не число
        return isNaN(num) ? 0 : num;
      }
        let v = r[sortColumn] ?? "";
        return typeof v === "string" ? v.toLowerCase() : v;
      };
        const va = getValue(a), vb = getValue(b);

        // если оба пустые — не меняем порядок
        if (va === null && vb === null) return 0;
        // если только a пустое — всегда после b
        if (va === null) return 1;
        // если только b пустое — всегда после a
        if (vb === null) return -1;

        // а теперь уже обычное сравнение дат/значений
        if (va < vb) return sortOrder === "asc" ? -1 : 1;
        if (va > vb) return sortOrder === "asc" ? 1 : -1;
        return 0;
      });
    

  const handleSort = (column) => {
    if (sortColumn === column) {
      if (sortOrder === "asc") setSortOrder("desc");
      else if (sortOrder === "desc") setSortOrder(null);
      else setSortOrder("asc");
    } else {
      setSortColumn(column);
      setSortOrder("asc");
    }
  };

  const toggleColumnVisibility = (key) => {
    setColumns((prev) =>
      prev.map((col) => (col.key === key ? { ...col, visible: !col.visible } : col))
    );
  };

  const moveColumn = (dragIndex, hoverIndex) => {
    setColumns((prevColumns) => {
      const visibleColumns = prevColumns.filter((col) => col.visible);
      const draggedColKey = visibleColumns[dragIndex].key;
      const hoverColKey = visibleColumns[hoverIndex].key;
      const realDragIndex = prevColumns.findIndex((col) => col.key === draggedColKey);
      const realHoverIndex = prevColumns.findIndex((col) => col.key === hoverColKey);
      const updatedColumns = [...prevColumns];
      const [movedColumn] = updatedColumns.splice(realDragIndex, 1);
      updatedColumns.splice(realHoverIndex, 0, movedColumn);
      localStorage.setItem("logistColumns", JSON.stringify(updatedColumns));
      return updatedColumns;
    });
  };

  // Компонент столбца для реализации drag & drop
  const Column = ({ index, column }) => {
    const ref = React.useRef(null);
    const [{ isDragging }, drag] = useDrag({
      type: COLUMN_TYPE,
      item: { index },
      collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    });
    const [, drop] = useDrop({
      accept: COLUMN_TYPE,
      hover: (draggedItem, monitor) => {
        const dragIndex = draggedItem.index;
        const hoverIndex = index;
        if (dragIndex === hoverIndex) return;
        const hoverBoundingRect = ref.current?.getBoundingClientRect();
        if (!hoverBoundingRect) return;
        const hoverMiddleX = (hoverBoundingRect.right - hoverBoundingRect.left) / 2;
        const mouseX = monitor.getClientOffset().x - hoverBoundingRect.left;
        if (dragIndex < hoverIndex && mouseX < hoverMiddleX) return;
        if (dragIndex > hoverIndex && mouseX > hoverMiddleX) return;
        moveColumn(dragIndex, hoverIndex);
        draggedItem.index = hoverIndex;
      },
    });
    drag(drop(ref));
    return (
      <th
        ref={ref}
        style={{
          cursor: "grab",
          opacity: isDragging ? 0.5 : 1,
          transition: "opacity 0.2s ease-in-out",
        }}
        onClick={() => handleSort(column.key)}
      >
        {column.label}{" "}
        {sortColumn === column.key
          ? sortOrder === "asc"
            ? "▲"
            : sortOrder === "desc"
            ? "▼"
            : ""
          : ""}
      </th>
    );
  };

  // Функция для подсчёта отсканированных марок по товарам квитанции
  const getMarksInfo = (receipt) => {
    let totalExpected = 0;
    let scannedCount = 0;
    receipt.products.forEach((product) => {
      if (product.is_marked) {
        totalExpected += product.actual_quantity;
        scannedCount += product.marks ? product.marks.length : 0;
      }
    });
    return { scannedCount, totalExpected };
  };

  // Обработчик клика по адресу доставки: устанавливает центр карты и переключает вид на "map"
  const handleAddressClick = (receipt) => {
    if (receipt.latitude && receipt.longitude) {
      const pos = [parseFloat(receipt.latitude), parseFloat(receipt.longitude)];
      setMapCenter(pos);
      setHighlightPosition(pos);
    } else {
      const defaultPos = [55.7558, 37.6173];
      setMapCenter(defaultPos);
      setHighlightPosition(defaultPos);
    }
    setViewMode("map");
  };

  const handleCombinedButtonClick = async (routeId, currentStatus) => {
    if (currentStatus === 'Открыт') {
      // Если маршрут открыт, закрываем его и устанавливаем готовность к выгрузке
      try {
        await api.patch(`/routes/${routeId}/status`, { route_status: 'Закрыт' });
        await api.patch(`/routes/mark-ready/${routeId}/true`);
        setRoutes(prevRoutes =>
          prevRoutes.map(r =>
            r.id === routeId ? { ...r, route_status: 'Закрыт', is_ready_for_upload: true } : r
          )
        );
      } catch (error) {
        console.error('Ошибка обновления маршрута:', error);
        alert('Не удалось закрыть маршрут и установить готовность к выгрузке.');
      }
    } else {
      // Если маршрут закрыт, открываем его и снимаем статус готовности
      try {
        await api.patch(`/routes/${routeId}/status`, { route_status: 'Открыт' });
        await api.patch(`/routes/mark-ready/${routeId}/false`);
        setRoutes(prevRoutes =>
          prevRoutes.map(r =>
            r.id === routeId ? { ...r, route_status: 'Открыт', is_ready_for_upload: false } : r
          )
        );
      } catch (error) {
        console.error('Ошибка обновления маршрута:', error);
        alert('Не удалось открыть маршрут.');
      }
    }
  };

return (
  <DndProvider backend={HTML5Backend}>
    <div className={styles.logistContainer}>
      {/* Боковое меню маршрутов */}
      <aside className={styles.logistSidebar}>
        <div className={styles.logistDateFilterRow}>
          <label>
            С:&nbsp;
            <input
              type="date"
              value={isNaN(startDate.getTime()) ? "" : startDate.toISOString().split("T")[0]}
              onChange={e => setStartDate(new Date(e.target.value))}
            />
          </label>
          <label>
            По:&nbsp;
            <input
              type="date"
              value={isNaN(endDate.getTime()) ? "" : endDate.toISOString().split("T")[0]}
              onChange={e => setEndDate(new Date(e.target.value))}
            />
          </label>
        </div>
        <ul className={styles.routesList}>
          {routes.map(route => {
            const { delivered, inProgress, undelivered, totalOrders } = getProgress(route.receipts);
            const dw = totalOrders > 0 ? (delivered / totalOrders) * 100 : 0;
            const iw = totalOrders > 0 ? (inProgress / totalOrders) * 100 : 0;
            const uw = totalOrders > 0 ? (undelivered / totalOrders) * 100 : 0;
            const isSelected = selectedRoute?.id === route.id;
            return (
              <li
                key={route.id}
                onClick={() => setSelectedRoute(route)}
                className={`${styles.routeItem} ${isSelected ? styles.selected : ""}`}
              >
                <div>
                  Маршрут №{route.route_number} | Водитель: {route.driver} | Грузчики: {route.loader1} {route.loader2} | Районы: {route.route_districts} ({route.route_date})
                </div>
                <button
                  className={`${styles.closeRouteButton} ${route.route_status === "Открыт" ? styles.closed : styles.open}`}
                  onClick={() => handleCombinedButtonClick(route.id, route.route_status)}
                >
                  {route.route_status === "Открыт" ? "Закрыть" : "Открыть"}
                </button>
                <div className={styles.progressContainer}>
                  <div className={styles.progressBar}>
                    <div className={styles.progressDelivered} style={{ width: `${dw}%` }} />
                    <div className={styles.progressInProgress} style={{ width: `${iw}%` }} />
                    <div className={styles.progressUndelivered} style={{ width: `${uw}%` }} />
                  </div>
                  <div className={styles.statusText}>
                    <span>{delivered}/{totalOrders} доставлено</span>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </aside>

      {/* Основная область: таблица или карта */}
      <main className={styles.routeDetails}>
        <div className={styles.viewToggle}>
          <button className={styles.smallButton} onClick={() => setViewMode(m => m === "table" ? "map" : "table")}>
            {viewMode === "table" ? "Показать карту" : "Показать таблицу"}
          </button>
                    <button
            className={styles.smallButton}
            onClick={() => {
              setTimelineScope('all');
              setShowTimeline(true);
            }}
            style={{ marginLeft: 8 }}
          >
            Timeline всех
          </button>
          <button className={styles.smallButton} onClick={() => setShowCouriers(v => !v)}>
            {showCouriers ? "Скрыть курьеров" : "Показать курьеров"}
          </button>
        </div>

        {selectedRoute ? (
          viewMode === "table" ? (
            <>
              <h2>Маршрут №{selectedRoute.route_number}</h2>
              <h3>
                Маршрут №{selectedRoute.route_number} | Водитель: {selectedRoute.driver} | Грузчики: {selectedRoute.loader1} {selectedRoute.loader2}
              </h3>
              <h3>Районы: {selectedRoute.route_districts}</h3>

              <input
                type="text"
                className={styles.searchInput2}
                placeholder="Введите текст для поиска..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />

              <button
                className={styles.smallButton}
                style={{ marginLeft: 8 }}
                onClick={() => {
                  const base = filteredReceipts
                    .filter(r => r.status_changed_at)
                    .slice()
                    .sort((a, b) => new Date(a.status_changed_at) - new Date(b.status_changed_at));
                  const diffs = {};
                  for (let i = 1; i < base.length; i++) {
                    diffs[base[i].id] = new Date(base[i].status_changed_at) - new Date(base[i - 1].status_changed_at);
                  }
                  setTimeDiffs(diffs);
                  setAnalysisMode(true);
                  setSortColumn("status_changed_at");
                  setSortOrder("asc");
                }}
              >
                Анализ
              </button>

          <button
            className={styles.smallButton}
            disabled={!selectedRoute}
            style={{ marginLeft: 8 }}
            onClick={() => {
              setTimelineScope('route');
              setShowTimeline(true);
            }}
          >
            Timeline маршрута
          </button>

              <div className={styles.columnSettings}>
                <h3>Настройка колонок</h3>
                {columns.map(col => (
                  <label key={col.key} style={{ marginRight: 12, display: "inline-block" }}>
                    <input
                      type="checkbox"
                      checked={col.visible}
                      onChange={() => toggleColumnVisibility(col.key)}
                    />
                    {' '}{col.label}
                  </label>
                ))}
              </div>

              <table className={styles.logistReceiptsTable}>
                <thead>
                  <tr>
                    {columns.filter(c => c.visible).map((col, i) => (
                      <Column key={col.key} index={i} column={col} />
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedReceipts.length > 0 ? sortedReceipts.map(receipt => {
                    const { scannedCount, totalExpected } = getMarksInfo(receipt);
                    return (
                      <tr key={receipt.id}>
                        {columns.filter(c => c.visible).map(col => {
                          let content;
                          if (col.key === "status_changed_at") {
                            content = receipt.status_changed_at
                              ? new Date(receipt.status_changed_at).toLocaleTimeString("ru-RU", {
                                  hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false
                                })
                              : "-";
                          } else if (col.key === "delta_time") {
                            content = analysisMode && timeDiffs[receipt.id] != null
                              ? formatDiff(timeDiffs[receipt.id])
                              : "";
                          } else if (col.key === "products") {
                              content = receipt.products
                                .map(p => {
                                  const time = p.updatedAt
                                    ? new Date(p.updatedAt)
                                        .toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit", second: "2-digit" })
                                    : "-";
                                  return `${p.product_name} x${p.actual_quantity} (${time})`;
                                })
                                .join(", ");
                            } else if (col.key === "is_check_issued") {
                            content = ["ПоСчету","Кредит","Взаимозачет","Дилер","БезОплаты"].includes(receipt.payment_type)
                              ? "Чек не нужен"
                              : receipt.is_check_refunded
                                ? "Возвращён"
                                : receipt.is_check_issued
                                  ? "Пробит"
                                  : "-";
                          } else if (col.key === "scannedCount") {
                            content = `${scannedCount} / ${totalExpected}`;
                          } else {
                            content = receipt[col.key];
                          }
                          return (
                            <td
                              key={col.key}
                              className={col.key === "status" ? styles.statusCell : ""}
                              style={col.key === "status" ? { backgroundColor: STATUS_COLORS[receipt.status] } : {}}
                              onClick={col.key === "delivery_address" ? () => handleAddressClick(receipt) : undefined}
                            >
                              {content}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  }) : (
                    <tr>
                      <td colSpan={columns.filter(c => c.visible).length} className={styles.noResults}>
                        Нет квитанций, соответствующих поиску.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </>
          ) : (
            <div className={styles.logistMapContainer}>
              <h2>Карта маршрута №{selectedRoute.route_number}</h2>
              <MapComponent
                className={styles.logistLeafletContainer}
                center={mapCenter}
                zoom={12}
                receipts={selectedRoute.receipts}
                onMarkerClick={handleMarkerClick}
                allowedUsersLocations={allowedUsersLocations}
                courierLocations={showCouriers ? filteredCouriers : []}
                highlightPosition={highlightPosition}
              />
            </div>
          )
        ) : (
          <p>Выберите маршрут для просмотра информации</p>
        )}
      </main>
    </div>
    {/* ↓ TimelineModal ↓ */}
    {showTimeline && timelineScope === 'route' && selectedRoute && (
      <TimelineModal
        // передаём один объект маршрута
        routes={undefined}
        receiptRoute={selectedRoute}
        includeProducts={includeProducts}
        onClose={() => setShowTimeline(false)}
        toggleIncludeProducts={setIncludeProducts}
      />
    )}

    {showTimeline && timelineScope === 'all' && (
      <TimelineModal
        // передаём массив всех маршрутов
        routes={routes.map(r => ({
          routeNumber: r.route_number,
          receipts: r.receipts
        }))}
        receiptRoute={undefined}
        includeProducts={includeProducts}
        onClose={() => setShowTimeline(false)}
        toggleIncludeProducts={setIncludeProducts}
      />
    )}

  </DndProvider>
);

};

export default LogistMain;
